import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getLeaderboard } from "@/lib/xp.functions";
import { supabase } from "@/integrations/supabase/client";

export type UserBadge = {
  rank: 1 | 2 | 3 | null;
  isPremium: boolean;
};

type Row = Awaited<ReturnType<typeof getLeaderboard>>[number];

/**
 * Global leaderboard subscription (top 200) used to drive avatar frames
 * (1st/2nd/3rd medals + Embaixador). Realtime invalidation on user_xp /
 * subscriptions changes keeps rank and premium status in sync.
 */
export function useUserBadgesData(): Row[] {
  const lb = useServerFn(getLeaderboard);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["user-badges-leaderboard"],
    queryFn: () => lb({ data: { limit: 200 } }),
    staleTime: 60_000,
  });

  useEffect(() => {
    const channelName = `user-badges-live-${Math.random().toString(36).slice(2)}`;
    const ch = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_xp" },
        () => qc.invalidateQueries({ queryKey: ["user-badges-leaderboard"] }),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions" },
        () => qc.invalidateQueries({ queryKey: ["user-badges-leaderboard"] }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [qc]);

  return q.data ?? [];
}

export function useUserBadge(
  userId?: string | null,
  fallback?: { isPremium?: boolean; rank?: number | null },
): UserBadge {
  const rows = useUserBadgesData();
  const row = userId ? rows.find((r) => r.user_id === userId) : undefined;
  const rank = (row?.rank ?? fallback?.rank ?? null) as number | null;
  const isPremium = row?.is_premium ?? !!fallback?.isPremium;
  return {
    rank: rank === 1 || rank === 2 || rank === 3 ? (rank as 1 | 2 | 3) : null,
    isPremium,
  };
}
